import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Plus, Trash2, PackageCheck, Boxes, Factory, Upload, CheckCircle, AlertTriangle, GitMerge } from 'lucide-react';
import { BasisCell, BasisCompact, ExpectedCell, ExpectedSummary, PlanBadge, SettingsCell, UtbCodeCell, planTitle } from './InventoryPlan';
import { opsApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

/**
 * Ops Inventory — the corrected Herborist domain model.
 *
 * Active Ants (AA) holds the finished, shippable stock — this is the
 * authoritative number for fulfilment and storefront availability.
 *
 * UTB is the upstream environment. It holds two unrelated kinds of
 * things:
 *   - finished goods that feed AA (mapped via SKU); and
 *   - UTB-only supplies (boxes, labels, bottles, packaging). These
 *     are never transferred to AA — "UTB only" is a first-class
 *     category, NOT an unmapped-error state.
 *
 * The UI reflects this directly: three sections, three purposes.
 * Mapping is only surfaced where it matters (finished goods).
 */
export default function OpsInventory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('finished'); // finished | utbOnly | replenish
  const [replenish, setReplenish] = useState(null);
  const [showUtbForm, setShowUtbForm] = useState(false);

  const load = useCallback(async () => {
    const { data } = await opsApi.inventory();
    setData(data);
  }, []);

  const loadReplenish = useCallback(async () => {
    try {
      const { data } = await opsApi.utbReplenishment();
      setReplenish(data);
    } catch {
      setReplenish(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
  }, [load]);

  useEffect(() => { loadReplenish(); }, [loadReplenish]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([load(), loadReplenish()]);
    setRefreshing(false);
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin" /></div>;
  if (!data) return null;

  const aa = data.aa || { connected: false, message: '' };
  const s = data.summary || {};

  return (
    <div data-testid="ops-inventory" className="p-8 max-w-[1680px] mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            <span className="font-semibold text-emerald-700">AA</span> holds finished, shippable stock (authoritative for fulfilment).
            <span className="ml-1"><span className="font-semibold text-indigo-700">UTB</span> holds upstream finished goods that feed AA via the shared SKU, plus UTB-only supplies (boxes, labels, packaging). UTB-only is a first-class category, not an error.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="aa-state" className={`text-[11px] px-2 py-1 rounded font-semibold ${aa.connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            {aa.connected ? `AA v3 · ${aa.message}` : `AA v3 OFF · ${aa.message}`}
          </span>
          <button data-testid="inventory-refresh" onClick={refresh} disabled={refreshing}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <SummaryCard label="Products in catalog" value={s.productCount} sub="isActive · non-archived" />
        <SummaryCard label="AA shippable units" value={s.aaTotalUnits ?? '—'} sub={aa.connected ? `${s.aaCoverage}/${s.productCount} SKUs at AA` : 'AA not connected'} accent="emerald" />
        <SummaryCard label="UTB finished (mapped)" value={s.utbFinishedUnits ?? 0} sub={`${s.utbFinishedMapped} SKUs · ${s.bridgeSourceCounts?.mapping || 0} via bridge`} accent="indigo" />
        <SummaryCard label="UTB raw & supplies" value={s.utbOnlyUnits ?? 0} sub={(() => {
          const sc = s.utbOnlySubtypeCounts || {};
          const parts = [];
          if (sc.raw_material) parts.push(`${sc.raw_material} raw`);
          if (sc.supply) parts.push(`${sc.supply} supplies`);
          if (sc.other_utb_only) parts.push(`${sc.other_utb_only} other`);
          return parts.join(' · ') || `${s.utbOnlyCount || 0} rows`;
        })()} accent="slate" />
        <SummaryCard label="AA-only valid" value={s.aaOnlyValidCount ?? 0} sub="No UTB equivalent · not produced" accent="amber" />
        <SummaryCard label="Inventory value (AA, excl. BTW)" value={formatCurrency(s.inventoryValueExclVat || 0)} sub="∑ AA stock × unit cost" />
      </div>

      <div className="border-b border-gray-200 mb-4 flex gap-1 flex-wrap">
        <TabButton id="finished" current={tab} set={setTab} icon={<PackageCheck className="w-3.5 h-3.5" />} label="Finished goods" count={s.productCount} />
        <TabButton id="utbOnly" current={tab} set={setTab} icon={<Boxes className="w-3.5 h-3.5" />} label="UTB only" count={s.utbOnlyCount} />
        <TabButton id="replenish" current={tab} set={setTab} icon={<Factory className="w-3.5 h-3.5" />} label="Aanvullen" count={replenish ? ((replenish.counts?.advice || 0) + (replenish.counts?.inbound_unknown || 0) + (replenish.counts?.check_settings || 0) + (replenish.counts?.check_code || 0)) : null} />
        <TabButton id="mapping" current={tab} set={setTab} icon={<GitMerge className="w-3.5 h-3.5" />} label="Mapping (AA↔UTB)" />
        <TabButton id="import" current={tab} set={setTab} icon={<Upload className="w-3.5 h-3.5" />} label="UTB import" />
      </div>

      {tab === 'finished' && <FinishedGoodsTable rows={data.finishedGoods} aaConnected={aa.connected} aaExpected={data.aaExpected} pending={data.utbFinishedPending || []} orphan={data.utbFinishedOrphan || data.utbUnlinkedFinished || []} />}
      {tab === 'utbOnly' && <UtbOnlyPanel rows={data.utbOnly || []} showForm={showUtbForm} setShowForm={setShowUtbForm} onChange={refresh} />}
      {tab === 'replenish' && <ReplenishmentPanel replenish={replenish} />}
      {tab === 'mapping' && <MappingPanel onChanged={refresh} />}
      {tab === 'import' && <UtbImportPanel onCommitted={refresh} />}
    </div>
  );
}


function SummaryCard({ label, value, sub, accent }) {
  const cls = accent === 'emerald' ? 'bg-emerald-50 border-emerald-200'
            : accent === 'indigo'  ? 'bg-indigo-50 border-indigo-200'
            : accent === 'slate'   ? 'bg-slate-50 border-slate-200'
            : accent === 'amber'   ? 'bg-amber-50 border-amber-200'
            : 'bg-white border-gray-200';
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}


function TabButton({ id, current, set, icon, label, count }) {
  const active = current === id;
  return (
    <button data-testid={`tab-${id}`} onClick={() => set(id)}
      className={`px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
        active ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-900'
      }`}>
      {icon}{label}
      {count != null && <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${active ? 'bg-indigo-100' : 'bg-gray-100'}`}>{count}</span>}
    </button>
  );
}


function FinishedGoodsTable({ rows, aaConnected, aaExpected, pending, orphan }) {
  return (
    <>
      <div className="mb-2 px-1"><ExpectedSummary aaExpected={aaExpected} /></div>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Product</th>
              <th className="px-4 py-3 text-left font-semibold" title="Canonical bridge key between AA and UTB">SKU</th>
              <th className="px-4 py-3 text-right font-semibold" title="Active Ants v3 — verzendbaar (/v3/stock) · daaronder beschikbaar (trigger) en fysiek aanwezig (max-basis) uit stockLevels">AA shippable · beschikbaar / fysiek</th>
              <th className="px-4 py-3 text-right font-semibold" title="UTB row mapped to this AA SKU">UTB (mapped)</th>
              <th className="px-4 py-3 text-left font-semibold" title="UTB-bestelcode: AA SupplierProductCode, of bestaande mapping als AA leeg is">UTB-code</th>
              <th className="px-4 py-3 text-right font-semibold" title="AA StockMinimum / StockMaximum · doosgrootte (OrderQuantity)">Min/Max · doos</th>
              <th className="px-4 py-3 text-left font-semibold" title="Aanvuladvies UTB→AA: onder minimum, volle dozen, maximum nooit overschreden, begrensd door UTB-voorraad">Voorstel</th>
              <th className="px-4 py-3 text-center font-semibold">Mapping</th>
              <th className="px-4 py-3 text-right font-semibold" title="Admin estimate held on products.stock — still drives checkout stock validation today. Not the fulfilment truth.">Admin est.</th>
              <th className="px-4 py-3 text-center font-semibold">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map(r => {
              const p = r.product;
              const aa = r.aaStock;
              const utb = r.utb;
              const utbQty = utb ? (utb.stock ?? 0) : null;
              const bridgeVia = utb?.bridgeSource?.startsWith('inventory_mappings') ? 'via bridge' : 'direct';
              const mappingBadge = !p.sku ? { lbl: 'no SKU', cls: 'bg-amber-100 text-amber-700', title: 'Data issue — product has no SKU' }
                                 : utb ? { lbl: `mapped · ${bridgeVia}`, cls: 'bg-emerald-100 text-emerald-700', title: `UTB ${utb.utbSku || '?'} → AA ${p.sku} (${utb.bridgeSource || 'direct'})` }
                                 : { lbl: 'AA only', cls: 'bg-slate-100 text-slate-600', title: 'Valid: no UTB equivalent needed (books, flyers, externally sourced items)' };
              return (
                <tr key={p._id} data-testid={`fg-row-${p._id}`} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <Link to={`/admin/products/${p._id}/edit`} className="font-medium text-gray-900 hover:text-indigo-700">{p.name_nl}</Link>
                    {p.netContent && <span className="ml-1 text-xs text-gray-400">· {p.netContent}</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{p.sku || <span className="text-amber-700">—</span>}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {aa === null || aa === undefined ? (
                      <span className="text-gray-300">{aaConnected ? 'absent' : '—'}</span>
                    ) : (
                      <span className={aa === 0 ? 'text-red-700 font-semibold' : aa < 10 ? 'text-amber-700 font-semibold' : 'text-gray-900'}>{aa}</span>
                    )}
                    <div className="mt-0.5"><BasisCompact plan={r.plan} /></div>
                    <div><ExpectedCell plan={r.plan} compact /></div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {utb ? <span className="text-indigo-700">{utbQty} <span className="text-[10px] text-gray-400">{utb.unit || ''}</span></span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3"><UtbCodeCell code={r.plan?.code} /></td>
                  <td className="px-4 py-3 text-right"><SettingsCell plan={r.plan} /></td>
                  <td className="px-4 py-3"><PlanBadge plan={r.plan} /></td>
                  <td className="px-4 py-3 text-center">
                    <span title={mappingBadge.title} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${mappingBadge.cls}`}>{mappingBadge.lbl}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-gray-500" title="Webshop-schatting (products.stock) — geen fulfilmentwaarheid, alleen verschilsignaal">{p.adminStock}</td>
                  <td className="px-4 py-3 text-center">
                    {p.isActive ? <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> : <span className="inline-block w-2 h-2 rounded-full bg-gray-300" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pending && pending.length > 0 && (
        <div data-testid="pending-utb" className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-2">UTB finished rows awaiting mapping ({pending.length})</p>
          <p className="text-xs text-slate-700 mb-2">
            Imported from the UTB stock list without an AA SKU declared yet. Neutral, expected state — map them later via the UTB admin form (set <code className="font-mono">AA SKU</code>) when the bridge is known, or leave them as "UTB only" supplies. They are <strong>not</strong> a data error.
          </p>
          <ul className="text-xs font-mono text-slate-700 space-y-0.5 max-h-40 overflow-auto">
            {pending.slice(0, 50).map(u => <li key={u._id}>• {u.utbSku} · {u.utbName} · stock {u.stock} {u.unit}</li>)}
            {pending.length > 50 && <li className="text-gray-400">… and {pending.length - 50} more</li>}
          </ul>
        </div>
      )}

      {orphan && orphan.length > 0 && (
        <div data-testid="orphan-utb" className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Mapping issue — UTB finished rows with a broken AA bridge ({orphan.length})
          </p>
          <p className="text-xs text-amber-700 mb-2">
            These rows declare an AA SKU that does not match any active product. Likely a data-entry typo — fix the <code className="font-mono">AA SKU</code> on the UTB row or on the product.
          </p>
          <ul className="text-xs font-mono text-amber-900 space-y-0.5">
            {orphan.map(u => <li key={u._id}>• {u.utbSku} → declared AA SKU <span className="underline">{u.aaSku || '∅'}</span> (name: {u.utbName})</li>)}
          </ul>
        </div>
      )}
    </>
  );
}


function UtbOnlyPanel({ rows, showForm, setShowForm, onChange }) {
  const [subFilter, setSubFilter] = useState('');  // '' | raw_material | supply | other_utb_only
  const subtypeCounts = rows.reduce((acc, r) => {
    const k = r.subtype || 'other_utb_only';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const visible = subFilter ? rows.filter(r => (r.subtype || 'other_utb_only') === subFilter) : rows;
  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs text-gray-500 max-w-2xl">
          Upstream UTB stock that never reaches AA as a standalone SKU: raw materials, packaging, labels, bottles, components. Subtype is derived from the row's active section or keyword hit and is view-only.
        </p>
        <button data-testid="utb-add-btn" onClick={() => setShowForm(s => !s)}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> {showForm ? 'Cancel' : 'Add UTB row'}
        </button>
      </div>
      <div data-testid="utb-subtype-filter" className="flex gap-1 text-xs mb-3">
        <FilterChip active={subFilter === ''} onClick={() => setSubFilter('')}>All <span className="opacity-60">({rows.length})</span></FilterChip>
        <FilterChip active={subFilter === 'raw_material'} onClick={() => setSubFilter('raw_material')} tone="emerald">Raw material <span className="opacity-60">({subtypeCounts.raw_material || 0})</span></FilterChip>
        <FilterChip active={subFilter === 'supply'} onClick={() => setSubFilter('supply')} tone="amber">Supply <span className="opacity-60">({subtypeCounts.supply || 0})</span></FilterChip>
        <FilterChip active={subFilter === 'other_utb_only'} onClick={() => setSubFilter('other_utb_only')}>Other <span className="opacity-60">({subtypeCounts.other_utb_only || 0})</span></FilterChip>
      </div>
      {showForm && <UtbForm defaultKind="utb_only" onSaved={() => { setShowForm(false); onChange(); }} />}
      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <p className="text-sm text-gray-500">{rows.length === 0 ? 'No UTB rows recorded yet.' : 'No rows match this subtype filter.'}</p>
          <p className="text-xs text-gray-400 mt-1">Import via XLSX or use <em>Add UTB row</em> above to seed manually.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">UTB SKU</th>
                <th className="px-4 py-3 text-left font-semibold">Name</th>
                <th className="px-4 py-3 text-left font-semibold">Subtype</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3 text-right font-semibold">Stock</th>
                <th className="px-4 py-3 text-left font-semibold">Source</th>
                <th className="px-4 py-3 text-left font-semibold">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map(r => {
                const subtypeTone = r.subtype === 'raw_material' ? 'bg-emerald-50 text-emerald-700'
                                  : r.subtype === 'supply' ? 'bg-amber-50 text-amber-800'
                                  : 'bg-slate-50 text-slate-700';
                return (
                  <tr key={r._id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-900 font-semibold">{r.utbSku}</td>
                    <td className="px-4 py-3">{r.utbName}</td>
                    <td className="px-4 py-3">
                      <span title={r.subtypeReason || ''} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${subtypeTone}`}>
                        {r.subtype || 'other_utb_only'}
                      </span>
                    </td>
                    <td className="px-4 py-3"><span className="text-[10px] font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{r.category || 'uncategorized'}</span></td>
                    <td className="px-4 py-3 text-right font-mono">{r.stock} <span className="text-[10px] text-gray-400">{r.unit}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{r.source}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{(r.updatedAt || '').slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`utb-del-${r._id}`} onClick={async () => { await opsApi.utbDelete(r._id); onChange(); }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete UTB row">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function UtbForm({ defaultKind, onSaved }) {
  const [form, setForm] = useState({
    utbSku: '', utbName: '', kind: defaultKind || 'utb_only',
    stock: 0, unit: 'pcs', aaSku: '', category: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = { ...form, stock: Number(form.stock) || 0 };
      if (form.kind === 'utb_only') payload.aaSku = '';
      await opsApi.utbUpsert(payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="utb-form" className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-4 mb-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="UTB SKU *">
          <input data-testid="utb-utbSku" value={form.utbSku} onChange={e => setForm({ ...form, utbSku: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono" />
        </Field>
        <Field label="Name *">
          <input data-testid="utb-utbName" value={form.utbName} onChange={e => setForm({ ...form, utbName: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-sm" />
        </Field>
        <Field label="Kind">
          <select data-testid="utb-kind" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-sm">
            <option value="utb_only">UTB only (supplies)</option>
            <option value="finished">Finished (feeds AA)</option>
          </select>
        </Field>
        <Field label="Category">
          <input data-testid="utb-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder={form.kind === 'finished' ? 'finished-good' : 'boxes / labels / bottles ...'} className="w-full px-2 py-1 border border-gray-200 rounded text-sm" />
        </Field>
        <Field label="Stock">
          <input data-testid="utb-stock" type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono" />
        </Field>
        <Field label="Unit">
          <input data-testid="utb-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-sm" />
        </Field>
        {form.kind === 'finished' && (
          <Field label="AA SKU (bridge)" hint="Canonical SKU on the AA finished good">
            <input data-testid="utb-aaSku" value={form.aaSku} onChange={e => setForm({ ...form, aaSku: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono" />
          </Field>
        )}
      </div>
      {err && <p data-testid="utb-form-err" className="text-xs text-red-600 mt-2">{err}</p>}
      <div className="mt-3 flex justify-end">
        <button data-testid="utb-save" onClick={save} disabled={saving || !form.utbSku || !form.utbName}
          className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}


function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
      {hint && <span className="block text-[10px] text-gray-400">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}


function ReplenishmentPanel({ replenish }) {
  if (!replenish) return <div className="text-sm text-gray-500 p-4" data-testid="replenish-loading">Aanvulplan laden…</div>;
  const c = replenish.counts || {};
  const b = replenish.buckets || {};
  const advice = b.advice || [];
  const inboundUnknown = b.inbound_unknown || [];
  const check = [...(b.check_settings || []), ...(b.check_code || [])];
  const blocked = [...(b.above_max || []), ...(b.utb_no_box || []), ...(b.no_box_fits || []), ...(b.unknown_stock || [])];
  return (
    <div className="space-y-5" data-testid="replenish-panel">
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <p className="text-xs text-gray-600">
          Regel per product (Benjamin 13-09): advies zodra <strong>AA beschikbaar &lt; minimum</strong> (beschikbaar = fysiek − vervallen/quarantaine − gereserveerd voor verzending). Het <strong>maximum is een harde grens op de fysieke voorraad</strong>: fysiek aanwezig (incl. vervallen, quarantaine en gereserveerd) + <strong>Verwacht bij AA</strong> (éénmaal) + voorstel ≤ max; alleen volle dozen (AA OrderQuantity), naar beneden afgerond; begrensd door UTB-voorraad in volle dozen. Staat de fysieke voorraad al op/boven max, dan géén hoeveelheid, ook als beschikbaar onder minimum ligt. Verwacht bij AA is wat na UTB-bevestiging in Maya is aangemeld: 0 = "niets aangemeld bij AA" — niet "geen openstaande UTB-bestelling"; geen transportbewijs, geen ETA. Is beschikbaar, fysiek of Verwacht onbekend, dan geen vrijgegeven hoeveelheid. Niets wordt automatisch besteld of overgebracht.
          {!replenish.aaConnected && <span className="ml-1 text-amber-700 font-semibold">AA niet bereikt — geen hoeveelheden.</span>}
        </p>
        <div className="mt-2"><ExpectedSummary aaExpected={replenish.aaExpected} /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="replenish-counts">
        <BucketCard label="Aanvullen" count={c.advice} accent="indigo" note="beschikbaar < min; volle dozen; fysiek + verwacht + voorstel ≤ max" />
        <BucketCard label="Onder min · verwacht onbekend" count={c.inbound_unknown} accent="amber" note="geen hoeveelheid vrijgegeven" />
        <BucketCard label="Controleren" count={(c.check_settings || 0) + (c.check_code || 0)} accent="amber" note="instellingen / UTB-code" />
        <BucketCard label="Geen doos mogelijk" count={(c.above_max || 0) + (c.utb_no_box || 0) + (c.no_box_fits || 0) + (c.unknown_stock || 0)} accent="slate" note="fysiek op/boven max / UTB te laag / ruimte < doos / onbekend" />
        <BucketCard label="OK" count={c.ok || 0} accent="emerald" note="beschikbaar op/boven minimum" />
      </div>
      <PlanList title="Aanvullen" rows={advice} testid="replenish-advice" empty="Geen vrijgegeven aanvulhoeveelheid (geen product onder minimum met bekende Verwacht bij AA en passende volle doos)." />
      <PlanList title="Onder minimum — Verwacht bij AA onbekend" rows={inboundUnknown} testid="replenish-inbound-unknown" empty="—" />
      <PlanList title="Eerst controleren" rows={check} testid="replenish-check" empty="Geen instellingen of codes te controleren." />
      <PlanList title="Geen doos mogelijk (fysiek op/boven max, UTB te laag, ruimte < doos, onbekend)" rows={blocked} testid="replenish-blocked" empty="—" collapsedDefault />
    </div>
  );
}

function PlanList({ title, rows, testid, empty, collapsedDefault }) {
  const [open, setOpen] = useState(!collapsedDefault);
  return (
    <div className="bg-white rounded-2xl border border-gray-100" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3 text-left">
        <span className="text-sm font-semibold text-gray-900">{title} <span className="text-gray-400 font-normal">({rows.length})</span></span>
        <span className="text-xs text-gray-400">{open ? 'verbergen' : 'tonen'}</span>
      </button>
      {open && (rows.length === 0 ? <p className="px-5 pb-4 text-xs text-gray-500">{empty}</p> : (
        <ul className="divide-y divide-gray-50">
          {rows.map(r => {
            const p = r.product; const plan = r.plan;
            return (
              <li key={p._id} className="px-5 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`plan-row-${p._id}`}>
                <span className="font-medium text-gray-900 min-w-[200px]">{p.name_nl}</span>
                <UtbCodeCell code={plan.code} />
                <BasisCell plan={plan} />
                <span className="font-mono text-xs text-gray-600" title="UTB beschikbaar (laatste import)">UTB {r.utb ? (r.utb.stock ?? 0) : '—'}</span>
                <ExpectedCell plan={plan} />
                <SettingsCell plan={plan} />
                <PlanBadge plan={plan} />
                <span className="text-xs text-gray-500 basis-full" title={planTitle(plan)}>{plan.reason}{plan.minShortfall > 0 && plan.status !== 'advice' ? ` · tekort tot minimum ${plan.minShortfall}` : ''}</span>
              </li>
            );
          })}
        </ul>
      ))}
    </div>
  );
}

function BucketCard({ label, count, note, accent }) {
  const tones = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[accent] || tones.slate}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-2xl font-bold mt-1">{count ?? 0}</p>
      <p className="text-[10px] opacity-75 mt-0.5">{note}</p>
    </div>
  );
}

function ClassificationChip({ label, count, tone, hint }) {
  const toneCls = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sky:     'bg-sky-50 text-sky-700 border-sky-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
  }[tone] || 'bg-gray-50 text-gray-700 border-gray-200';
  return (
    <span title={hint || ''}
          data-testid={`classification-chip-${label}`}
          className={`px-2 py-0.5 rounded-full border text-[11px] ${toneCls}`}>
      <span className="font-mono">{label}</span>: <span className="font-semibold">{count}</span>
    </span>
  );
}

function DiagStat({ label, value, hint, tone }) {
  const toneCls = {
    emerald: 'text-emerald-700',
    sky:     'text-sky-700',
    amber:   'text-amber-800',
    slate:   'text-slate-700',
  }[tone] || 'text-gray-900';
  return (
    <div title={hint || ''}
         className="rounded border border-gray-200 bg-white px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`text-sm font-semibold font-mono ${toneCls}`}>{value}</p>
    </div>
  );
}


// ─── Mapping panel (AA↔UTB finished-goods bridge) ─────────────────────
//
// Imports the operator-maintained mapping CSV and shows the resulting
// bridge rows side-by-side with their resolved status. Snapshot-replace
// semantics: re-uploading for the same `source` tag drops the prior
// snapshot.
function MappingPanel({ onChanged }) {
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState('csv:mapping-table-v1');
  const [filter, setFilter] = useState('');       // mapped | orphan | ''
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [{ data: d }, { data: l }] = await Promise.all([
        opsApi.mappingDashboard(),
        opsApi.mappingList({ limit: 1000, status: filter || '' }),
      ]);
      setDashboard(d); setRows(l);
    } catch (e) {
      setErr(`Load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [filter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true); setErr(''); setCommitResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await opsApi.mappingPreview(fd);
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await opsApi.mappingCommit(fd, { source });
      setCommitResult(data);
      setPreview(null); setFile(null); if (fileRef.current) fileRef.current.value = '';
      await loadAll();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const clearSource = async (src) => {
    if (!window.confirm(`Clear all mapping rows for source "${src}"? This cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      await opsApi.mappingClearSource(src);
      await loadAll();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="mapping-panel" className="space-y-6">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Mapping rows" value={dashboard.total} />
          <MiniStat label="Mapped" value={dashboard.mapped} tone="emerald" />
          <MiniStat label="Orphan" value={dashboard.orphan} tone="amber" />
          <MiniStat label="Sources" value={(dashboard.sources || []).length} sub={(dashboard.sources || []).join(' · ') || '—'} />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-gray-900">Import mapping CSV</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Bridge CSV columns: <code className="font-mono">utb_artikel</code> / <code className="font-mono">aa_sku</code> (required), <code className="font-mono">utb_name</code> / <code className="font-mono">aa_name</code> / <code className="font-mono">match_score</code> (optional). Semicolon- or comma-delimited.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-gray-500">Source tag
              <input value={source} onChange={e => setSource(e.target.value)}
                     className="ml-2 border border-gray-200 rounded px-2 py-1 text-xs font-mono w-56" />
            </label>
          </div>
        </div>
        <div className="mt-3 flex gap-2 items-center flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt"
                 onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setCommitResult(null); }}
                 data-testid="mapping-file-input"
                 className="text-xs" />
          <button onClick={doPreview} disabled={!file || busy}
                  data-testid="mapping-preview-btn"
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-xs rounded">
            Preview
          </button>
          <button onClick={doCommit} disabled={!file || busy || !preview?.ok}
                  data-testid="mapping-commit-btn"
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs rounded">
            Commit (replace snapshot)
          </button>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 align-middle" />
          <span className="italic">Preview writes nothing.</span> Commit REPLACES all prior rows for the same source tag.
        </p>
        {err && <p data-testid="mapping-err" className="text-xs text-red-600 mt-2">{err}</p>}
        {preview && !commitResult && (
          <div data-testid="mapping-preview" className={`mt-3 rounded-lg p-3 border ${preview.ok ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
            <p className="text-xs font-semibold text-blue-900">{preview.message}</p>
            {preview.conflicts && (Object.keys(preview.conflicts.multiTargetAaSku || {}).length > 0 || Object.keys(preview.conflicts.multiUtbSku || {}).length > 0) && (
              <div className="mt-2 text-[11px] text-amber-800">
                {Object.keys(preview.conflicts.multiTargetAaSku || {}).length > 0 && (
                  <p>M:1 — {Object.keys(preview.conflicts.multiTargetAaSku).length} AA SKU(s) receive multiple UTB pointers.</p>
                )}
                {Object.keys(preview.conflicts.multiUtbSku || {}).length > 0 && (
                  <p>1:M — {Object.keys(preview.conflicts.multiUtbSku).length} UTB SKU(s) map to multiple AA targets.</p>
                )}
              </div>
            )}
            {preview.errors?.length > 0 && (
              <ul className="mt-2 text-[11px] text-amber-800 space-y-0.5">
                {preview.errors.slice(0, 10).map((e, i) => <li key={`${e.row}-${i}`}>• row {e.row}: {e.message}</li>)}
              </ul>
            )}
          </div>
        )}
        {commitResult && (
          <div data-testid="mapping-commit-result" className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
            <p className="font-semibold">Snapshot replaced for <code className="font-mono">{commitResult.source}</code>.</p>
            <p className="mt-0.5">Deleted {commitResult.replacedRowCount} prior row(s); inserted {commitResult.insertedRowCount}. Resolved: {commitResult.resolved?.mapped} mapped · {commitResult.resolved?.orphan} orphan.</p>
          </div>
        )}
      </div>

      <AaMasterImportCard onChanged={() => { loadAll(); onChanged && onChanged(); }} />

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <p className="text-sm font-semibold text-gray-900">Mapping rows {rows ? <span className="text-gray-500 text-xs">({rows.count})</span> : ''}</p>
          <div className="flex gap-1 text-xs items-center">
            <FilterChip active={filter === ''} onClick={() => setFilter('')}>All</FilterChip>
            <FilterChip active={filter === 'mapped'} onClick={() => setFilter('mapped')} tone="emerald">Mapped</FilterChip>
            <FilterChip active={filter === 'orphan'} onClick={() => setFilter('orphan')} tone="amber">Orphan</FilterChip>
            {dashboard?.sources?.map(src => (
              <button key={src} onClick={() => clearSource(src)}
                      className="ml-2 text-[10px] text-red-600 hover:underline" title={`Clear snapshot ${src}`}>
                <Trash2 className="w-3 h-3 inline-block mr-0.5" />{src}
              </button>
            ))}
          </div>
        </div>
        {!rows ? <p className="text-xs text-gray-500">Loading…</p> : rows.count === 0 ? (
          <p className="text-xs text-gray-500">No mapping rows. Upload the Mapping-Tabel CSV above to populate the bridge.</p>
        ) : (
          <div className="overflow-auto max-h-[60vh] border border-gray-100 rounded">
            <table className="w-full text-[11px]">
              <thead className="text-[10px] text-gray-400 uppercase bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left">UTB SKU</th>
                  <th className="px-2 py-1.5 text-left">UTB name</th>
                  <th className="px-2 py-1.5 text-left">AA SKU</th>
                  <th className="px-2 py-1.5 text-left">Product (catalog)</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-right">Score</th>
                  <th className="px-2 py-1.5 text-left">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.rows.map(r => (
                  <tr key={r._id} className="hover:bg-gray-50/40">
                    <td className="px-2 py-1 font-mono font-semibold">{r.utbSku}</td>
                    <td className="px-2 py-1">{r.utbName || '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.aaSku}</td>
                    <td className="px-2 py-1">{r.productName || <span className="text-gray-400 italic">no product</span>}</td>
                    <td className="px-2 py-1">
                      <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${r.resolvedStatus === 'mapped' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
                        {r.resolvedStatus}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-gray-500">{typeof r.matchScore === 'number' ? r.matchScore.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-gray-500">{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, tone }) {
  const toneCls = tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : tone === 'amber'   ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-white border-gray-200 text-gray-900';
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneCls}`}>
      <p className="text-[9px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-lg font-bold">{value}</p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function FilterChip({ active, onClick, tone, children }) {
  const cls = active
    ? (tone === 'emerald' ? 'bg-emerald-600 text-white' : tone === 'amber' ? 'bg-amber-600 text-white' : 'bg-gray-900 text-white')
    : 'bg-gray-100 hover:bg-gray-200 text-gray-700';
  return (
    <button onClick={onClick} className={`px-2 py-0.5 rounded ${cls}`}>{children}</button>
  );
}

// ─── AA Product Master one-shot importer ──────────────────────────────
//
// Admin-only utility (NOT a user-facing flow). Uploads an Active Ants
// product-export XLSX, previews impact, then upserts. New SKUs land
// with isActive=false — operator must explicitly publish them.
function AaMasterImportCard({ onChanged }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setCommitResult(null); setErr(''); if (fileRef.current) fileRef.current.value = ''; };

  const doPreview = async () => {
    if (!file) return;
    setBusy(true); setErr(''); setCommitResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await opsApi.aaMasterPreview(fd);
      setPreview(data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await opsApi.aaMasterCommit(fd);
      setCommitResult(data);
      setPreview(null); setFile(null); if (fileRef.current) fileRef.current.value = '';
      onChanged && onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="aa-master-import-card" className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-gray-900">AA Product Master Import <span className="text-[10px] font-mono bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded ml-2">admin utility</span></p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            One-shot upsert from an Active Ants <code className="font-mono">Export.xlsx</code> snapshot. Operational fields (SKU, barcode, dimensions, weight, harmonization, country, AA stock min/max) are written. Commercial fields (name, slug, description, price, images, SEO) are NEVER overwritten on existing products. New SKUs land as <code className="font-mono">isActive=false</code> — they will NOT appear in the storefront until published.
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2 items-center flex-wrap">
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm"
               onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setCommitResult(null); }}
               data-testid="aa-master-file-input" className="text-xs" />
        <button onClick={doPreview} disabled={!file || busy}
                data-testid="aa-master-preview-btn"
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-xs rounded">
          Preview
        </button>
        <button onClick={doCommit} disabled={!file || busy || !preview?.ok}
                data-testid="aa-master-commit-btn"
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-xs rounded">
          Upsert (no delete, no archive)
        </button>
        {(file || preview || commitResult) && (
          <button onClick={reset} className="text-xs text-gray-500 hover:text-gray-900">Clear</button>
        )}
      </div>
      <p className="text-[11px] text-gray-500 mt-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 align-middle" />
        <span className="italic">Preview writes nothing.</span> Commit upserts only — never deletes / never archives products absent from the file.
      </p>
      {err && <p data-testid="aa-master-err" className="text-xs text-red-600 mt-2">{err}</p>}
      {preview && !commitResult && (
        <div data-testid="aa-master-preview" className={`mt-3 rounded-lg p-3 border ${preview.ok ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
          <p className="text-xs font-semibold text-blue-900">{preview.message}</p>
          {preview.summary && (
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <DiagStat label="Total rows"          value={preview.summary.totalRows} />
              <DiagStat label="Valid SKU rows"      value={preview.summary.validSkuRows} />
              <DiagStat label="To create (new)"     value={preview.summary.toCreate} tone="emerald" />
              <DiagStat label="To update (existing)" value={preview.summary.toUpdate} />
              <DiagStat label="Will lift orphans"   value={preview.summary.willLiftOrphanCount} tone="emerald" hint="Mapping rows that will become mapped after these creates" />
              <DiagStat label="AA-only candidates"  value={preview.summary.aaOnlyCandidatesCount} tone="amber" hint="New AA SKUs with no UTB equivalent in the bridge — externally sourced or upcoming items" />
              <DiagStat label="Missing barcode"     value={preview.summary.missingBarcode} tone="amber" />
              <DiagStat label="UTB-code (SupplierProductCode)" value={preview.summary.hasSupplierProductCode ? `${preview.summary.supplierCodeFilled} gevuld · ${preview.summary.supplierCodeMissing} leeg` : 'kolom ontbreekt'} tone={preview.summary.hasSupplierProductCode ? 'emerald' : 'amber'} hint={preview.summary.hasSupplierProductCode ? 'Wordt als apart veld utbSupplierCode opgeslagen; nooit in SKU/barcode/mapping. Lege cel = geen code (zichtbaar als "ontbreekt").' : 'Bestaande UTB-codes blijven ongewijzigd; bestaande mappings worden niet gewist.'} />
              <DiagStat label="Missing dimensions"  value={preview.summary.missingDimensions} tone="amber" />
              <DiagStat label="Missing weight"      value={preview.summary.missingWeight} tone="amber" />
              <DiagStat label="Duplicate SKU warns" value={preview.summary.duplicateSkuWarnings} tone="amber" />
            </div>
          )}
          {preview.summary?.willLiftOrphanSkus?.length > 0 && (
            <p className="text-[10px] text-emerald-700 mt-2">
              First lifted orphans: <span className="font-mono">{preview.summary.willLiftOrphanSkus.slice(0, 8).join(' · ')}{preview.summary.willLiftOrphanSkus.length > 8 ? ' …' : ''}</span>
            </p>
          )}
        </div>
      )}
      {commitResult && (
        <div data-testid="aa-master-commit-result" className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
          <p className="font-semibold">Imported. {commitResult.createdCount} created · {commitResult.updatedCount} updated · {commitResult.costFilledCount} cost-filled · {commitResult.errorCount} error(s)</p>
          {commitResult.mappingReResolution && (
            <p className="mt-0.5">
              Mapping bridge re-resolved: mapped {commitResult.mappingReResolution.beforeMapped} → {commitResult.mappingReResolution.afterMapped}, orphan {commitResult.mappingReResolution.beforeOrphan} → {commitResult.mappingReResolution.afterOrphan} ({commitResult.mappingReResolution.promotedCount} promoted).
            </p>
          )}
          {commitResult.errorCount > 0 && commitResult.errors?.length > 0 && (
            <ul className="mt-2 text-[11px] text-amber-800 space-y-0.5 list-disc pl-5">
              {commitResult.errors.slice(0, 8).map((e, i) => <li key={`${e.sku}-${e.row}`}>SKU {e.sku} (row {e.row}): {e.message}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function UtbImportPanel({ onCommitted }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await opsApi.utbImportHistory({ limit: 10 });
      setHistory(data?.runs || []);
    } catch { setHistory([]); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f); setPreview(null); setCommitResult(null); setErr('');
  };

  const doPreview = async () => {
    if (!file) return;
    setErr(''); setPreview(null); setCommitResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await opsApi.utbImportPreview(fd);
      setPreview(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Preview failed');
    }
  };

  const doCommit = async () => {
    if (!file) return;
    setCommitting(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await opsApi.utbImportCommit(fd);
      setCommitResult(data);
      await loadHistory();
      onCommitted && onCommitted();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    setFile(null); setPreview(null); setCommitResult(null); setErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div data-testid="utb-import-panel">
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">UTB Excel import</h3>
        <p className="text-xs text-gray-500 mb-4">
          Upload a <code className="font-mono">.xlsx</code>/<code className="font-mono">.xlsm</code> UTB stock export. First column should be a SKU alias (<code className="font-mono">SKU</code>, <code className="font-mono">Artikelcode</code>, …) and there must be a name column (<code className="font-mono">Naam</code>, <code className="font-mono">Description</code>, …). When an <code className="font-mono">AA SKU</code>/<code className="font-mono">EAN</code> column is filled the row is treated as a finished good that feeds AA via the canonical bridge; otherwise it's a UTB-only supply row.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="utb-import-file" ref={fileRef} type="file" accept=".xlsx,.xlsm"
            onChange={onPick}
            className="text-xs file:px-3 file:py-1.5 file:border-0 file:rounded file:bg-indigo-600 file:text-white file:font-semibold file:hover:bg-indigo-700" />
          <button data-testid="utb-import-preview-btn" onClick={doPreview} disabled={!file}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">
            Preview
          </button>
          <button data-testid="utb-import-commit-btn" onClick={doCommit} disabled={!file || committing || !preview?.ok}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {committing ? 'Committing…' : 'Commit'}
          </button>
          {(file || preview || commitResult) && (
            <button onClick={reset} className="text-xs text-gray-500 hover:text-gray-900">Clear</button>
          )}
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 align-middle" />
          <span className="italic">Preview writes nothing to the database.</span> Only Commit saves rows to <code className="font-mono">utb_stock</code>.
        </p>
        {err && <p data-testid="utb-import-err" className="text-xs text-red-600 mt-3">{err}</p>}

        {commitResult && (
          <div data-testid="utb-import-commit-result" className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Import #{commitResult.importId?.slice(-6)} committed</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              {commitResult.inserted} inserted · {commitResult.updated} updated · {commitResult.errors?.length || 0} errors · {commitResult.parseErrors?.length || 0} parse warnings
            </p>
            {commitResult.mapping && (
              <p className="text-xs text-emerald-800 mt-1">
                After mapping: <strong>{commitResult.mapping.finishedMapped}</strong> mapped · <strong>{commitResult.mapping.finishedPending}</strong> awaiting mapping · <strong>{commitResult.mapping.finishedOrphan}</strong> broken bridge · <strong>{commitResult.mapping.utbOnly}</strong> UTB-only
              </p>
            )}
          </div>
        )}

        {preview && !commitResult && (
          <div data-testid="utb-import-preview" className={`mt-4 rounded-lg p-3 border ${preview.ok ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
            <p className="text-xs font-semibold flex items-center gap-1.5">
              {preview.ok
                ? <><CheckCircle className="w-3.5 h-3.5 text-blue-700" /> <span className="text-blue-800">{preview.message}</span></>
                : <><AlertTriangle className="w-3.5 h-3.5 text-red-700" /> <span className="text-red-800">{preview.message}</span></>}
            </p>
            {preview.headersDetected && (
              <p className="text-[10px] text-gray-500 mt-1 font-mono">
                Headers detected: {preview.headersDetected.join(' · ')}
              </p>
            )}

            {/* Preview diagnostics — consolidated numeric summary. */}
            {preview.ok && (
              <div data-testid="utb-import-diagnostics"
                   className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-[11px]">
                <DiagStat label="Source rows" value={preview.rawRowCount ?? 0} />
                <DiagStat label="Logical rows" value={preview.rowCount ?? 0} hint="After duplicate aggregation" />
                <DiagStat label="Duplicates aggregated" value={preview.aggregatedDuplicateCount ?? 0} />
                <DiagStat label="Section headers" value={preview.sectionHeaderCount ?? 0} hint="Excluded structural rows" />
                <DiagStat label="Finished pending" value={preview.classificationCounts?.finished_pending ?? 0} tone="sky" />
                <DiagStat label="UTB only" value={preview.classificationCounts?.utb_only ?? 0} tone="slate" />
                <DiagStat label="Finished mapped" value={preview.classificationCounts?.finished_mapped ?? 0} tone="emerald" />
                <DiagStat label="Finished orphan" value={preview.classificationCounts?.finished_orphan ?? 0} tone="amber" />
              </div>
            )}

            {/* Commit safety warnings — soft, commit stays enabled. */}
            {preview.ok && (() => {
              const warnings = [];
              const logical = preview.rowCount ?? 0;
              const utbOnly = preview.classificationCounts?.utb_only ?? 0;
              const finishedPending = preview.classificationCounts?.finished_pending ?? 0;
              if ((preview.sectionHeaderCount ?? 0) === 0) {
                warnings.push('No section headers detected. Files without "Grondstoffen" / "Afgewerkte producten" dividers cannot promote rows to finished_pending — double-check your file structure.');
              }
              if (finishedPending === 0 && logical > 0) {
                warnings.push('Zero rows classified as finished_pending. That\'s normal for a supplies-only sheet, but unusual for a full UTB stock export.');
              }
              if (logical > 0 && (utbOnly / logical) > 0.8) {
                const pct = Math.round((utbOnly / logical) * 100);
                warnings.push(`${pct}% of parsed rows are utb_only. If this was supposed to be a finished-goods import, the "Afgewerkte producten" section may be missing or mis-named.`);
              }
              if (!warnings.length) return null;
              return (
                <div data-testid="utb-import-commit-warnings"
                     className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-[11px] font-semibold text-amber-900 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Review before committing — {warnings.length === 1 ? '1 safety check' : `${warnings.length} safety checks`} flagged
                  </p>
                  <ul className="mt-1.5 text-[11px] text-amber-900 space-y-1 list-disc pl-5">
                    {warnings.map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}
                  </ul>
                  <p className="text-[10px] text-amber-800 mt-2 italic">
                    These are warnings, not blockers. You can still proceed with Commit, but the preview suggests the file may not match the expected UTB export shape.
                  </p>
                </div>
              );
            })()}

            {preview.classificationCounts && (
              <div data-testid="utb-import-classification-summary" className="mt-2 flex flex-wrap gap-2 text-[11px]">
                <ClassificationChip label="finished_mapped"  count={preview.classificationCounts.finished_mapped  ?? 0} tone="emerald" hint="AA bridge resolves to an active product" />
                <ClassificationChip label="finished_pending" count={preview.classificationCounts.finished_pending ?? 0} tone="sky"     hint="Finished good under Afgewerkte producten section or with explicit kind" />
                <ClassificationChip label="finished_orphan"  count={preview.classificationCounts.finished_orphan  ?? 0} tone="amber"   hint="AA SKU declared but no matching product" />
                <ClassificationChip label="utb_only"         count={preview.classificationCounts.utb_only         ?? 0} tone="slate"   hint="Upstream stock: raw materials / labels / packaging / supplies" />
                {typeof preview.aggregatedDuplicateCount === 'number' && preview.aggregatedDuplicateCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {preview.aggregatedDuplicateCount} duplicate row(s) aggregated
                  </span>
                )}
                {typeof preview.sectionHeaderCount === 'number' && preview.sectionHeaderCount > 0 && (
                  <span data-testid="utb-import-section-headers" className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                    {preview.sectionHeaderCount} section header(s) excluded
                  </span>
                )}
              </div>
            )}
            {preview.sectionHeadersExcluded && preview.sectionHeadersExcluded.length > 0 && (
              <div className="mt-2 text-[10px] text-violet-700 font-mono">
                Sections seen: {preview.sectionHeadersExcluded.map(s => `${s.label} (row ${s.row} → ${s.sectionKind})`).join(' · ')}
              </div>
            )}
            {preview.errors && preview.errors.length > 0 && (
              <ul className="mt-2 text-[11px] text-amber-800 space-y-0.5">
                {preview.errors.slice(0, 15).map((e, i) => <li key={`${e.row}-${i}`}>• row {e.row}: {e.message}</li>)}
                {preview.errors.length > 15 && <li className="text-gray-500">... and {preview.errors.length - 15} more</li>}
              </ul>
            )}
            {preview.rows && preview.rows.length > 0 && (
              <div className="mt-3 overflow-auto max-h-96 bg-white rounded border border-gray-100">
                <table className="w-full text-[11px]">
                  <thead className="text-[10px] text-gray-400 uppercase sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left">UTB SKU</th>
                      <th className="px-2 py-1.5 text-left">Name</th>
                      <th className="px-2 py-1.5 text-left">Classification</th>
                      <th className="px-2 py-1.5 text-left">Section</th>
                      <th className="px-2 py-1.5 text-right">Stock</th>
                      <th className="px-2 py-1.5 text-left">AA SKU</th>
                      <th className="px-2 py-1.5 text-left">Source rows</th>
                      <th className="px-2 py-1.5 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.rows.slice(0, 200).map(r => {
                      const tone = {
                        finished_mapped:  'bg-emerald-100 text-emerald-700',
                        finished_pending: 'bg-sky-100 text-sky-700',
                        finished_orphan:  'bg-amber-100 text-amber-700',
                        utb_only:         'bg-slate-100 text-slate-700',
                      }[r.classification] || 'bg-gray-100 text-gray-700';
                      const agg = Array.isArray(r.aggregatedFromRows) ? r.aggregatedFromRows : [r.sourceRow];
                      return (
                        <tr key={r.utbSku} className="hover:bg-gray-50/40">
                          <td className="px-2 py-1 font-mono font-semibold">{r.utbSku}</td>
                          <td className="px-2 py-1">{r.utbName}</td>
                          <td className="px-2 py-1">
                            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${tone}`}>{r.classification}</span>
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px] text-violet-700">
                            {r.activeSectionLabel || (r.activeSection ? r.activeSection : '—')}
                          </td>
                          <td className="px-2 py-1 text-right font-mono">{r.stock}</td>
                          <td className="px-2 py-1 font-mono text-gray-600">{r.aaSku || '—'}</td>
                          <td className="px-2 py-1 font-mono text-[10px] text-gray-500">
                            {agg.length > 1 ? `${agg[0]}…${agg[agg.length - 1]} (${agg.length})` : agg[0]}
                          </td>
                          <td className="px-2 py-1 text-[10px] text-gray-500">{r.classificationReason || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {preview.ok && (
              <div data-testid="utb-import-legend"
                   className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-[10.5px] text-gray-600 leading-relaxed">
                <p className="font-semibold text-gray-700 mb-1">How to read this preview</p>
                <ul className="space-y-0.5 list-disc pl-5">
                  <li><span className="font-mono text-sky-700">finished_pending</span> = upstream finished goods at UTB, not yet bridged to AA. These will drive replenishment planning once a bridge is mapped.</li>
                  <li><span className="font-mono text-emerald-700">finished_mapped</span> = finished goods whose AA SKU already resolves to an active product. Ready for warehouse sync comparisons.</li>
                  <li><span className="font-mono text-amber-700">finished_orphan</span> = AA SKU declared on the row but no matching product in the catalogue. Fix the AA SKU or archive the row.</li>
                  <li><span className="font-mono text-slate-700">utb_only</span> = raw materials, packaging, labels, supplies, or other non-AA rows. These stay upstream and do NOT feed AA.</li>
                  <li><span className="text-violet-700">Section headers</span> (e.g. <em>Grondstoffen</em>, <em>Afgewerkte producten</em>) are excluded structural rows — NOT inventory rows.</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Recent imports</h3>
        </div>
        {history.length === 0 ? (
          <p className="px-5 py-6 text-xs text-gray-400 italic text-center">No imports yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 text-gray-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Imported</th>
                <th className="px-4 py-2 text-left">File</th>
                <th className="px-4 py-2 text-right">Rows</th>
                <th className="px-4 py-2 text-right">Inserted</th>
                <th className="px-4 py-2 text-right">Updated</th>
                <th className="px-4 py-2 text-right">Errors</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {history.map(r => (
                <tr key={r.importId} className="hover:bg-gray-50/40">
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">{(r.importedAt || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-2 text-xs">{r.filename}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.rowCount}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-700">{r.inserted}</td>
                  <td className="px-4 py-2 text-right font-mono text-blue-700">{r.updated}</td>
                  <td className={`px-4 py-2 text-right font-mono ${r.errorCount ? 'text-red-700' : 'text-gray-400'}`}>{r.errorCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

